import React, { useState, useEffect, useMemo } from 'react'
import Header from '../../layouts/Header'
import { useNavigate } from 'react-router-dom';
import { Button, Card, Col, Form, Row, Spinner, Table, Badge, InputGroup } from 'react-bootstrap';
import { ArrowLeft, FileEarmarkSpreadsheet, Search } from 'react-bootstrap-icons';
import DatePicker from "react-datepicker";
import Select from 'react-select';
import axios from '../../api/axios';
import { toast } from 'react-toastify';

// Custom styles for react-select
const selectStyles = {
    control: (provided, state) => ({
        ...provided,
        borderColor: state.isFocused ? '#86b7fe' : '#6c757d',
        minHeight: '38px',
        boxShadow: state.isFocused ? '0 0 0 0.25rem rgba(13,110,253,.25)' : 'none',
        '&:hover': { borderColor: '#6c757d' }
    }),
    groupHeading: (provided) => ({
        ...provided,
        fontSize: '0.7rem',
        fontWeight: '700',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        padding: '6px 12px 4px',
        color: '#6c757d',
        borderBottom: '1px solid #e9ecef',
        marginBottom: '4px',
    }),
    option: (provided, state) => ({
        ...provided,
        fontSize: '0.875rem',
        backgroundColor: state.isSelected
            ? '#0d6efd'
            : state.isFocused
            ? '#e8f0fe'
            : 'white',
        color: state.isSelected ? 'white' : '#212529',
        cursor: 'pointer',
    }),
    singleValue: (provided, state) => ({
        ...provided,
        color: state.data?.status !== 'ACTIVE' ? '#dc3545' : '#212529',
    }),
};

// Format option label with status badge inline
const formatOptionLabel = ({ label, status }) => (
    <div className="d-flex align-items-center justify-content-between">
        <span>{label}</span>
        {status && status !== 'ACTIVE' && (
            <span
                style={{
                    fontSize: '0.65rem',
                    fontWeight: '600',
                    padding: '1px 7px',
                    borderRadius: '10px',
                    backgroundColor: status === 'INACTIVE' ? '#dc354520' : '#fd7e1420',
                    color: status === 'INACTIVE' ? '#dc3545' : '#fd7e14',
                    border: `1px solid ${status === 'INACTIVE' ? '#dc354540' : '#fd7e1440'}`,
                    marginLeft: '8px',
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                }}
            >
                {status === 'INACTIVE' ? 'Inactive' : 'Coming Soon'}
            </span>
        )}
    </div>
);

const OutletInventoryReport = () => {

    const currentSkin = localStorage.getItem("skin-mode") ? "dark" : "";
    const [skin, setSkin] = useState(currentSkin);
    const navigate = useNavigate();

    const [startDate, setStartDate] = useState(null);
    const [selectedOutlet, setSelectedOutlet] = useState(null);
    const [outlets, setOutlets] = useState([]);
    const [inventoryReportData, setInventoryReportData] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");

    const [downloadBtnLoading, setDownloadBtnLoading] = useState(false);
    const [loading, setLoading] = useState(false);
    const [clicked, setClicked] = useState(false);

    const accessToken = localStorage.getItem("authToken");

    // Build grouped options: Active on top, then Inactive, then Coming Soon
    const outletOptions = useMemo(() => {
        const active = outlets
            .filter((o) => o.status === 'ACTIVE')
            .map((o) => ({ value: o._id, label: `${o.name} (${o.city})`, status: o.status }));

        const inactive = outlets
            .filter((o) => o.status === 'INACTIVE')
            .map((o) => ({ value: o._id, label: `${o.name} (${o.city})`, status: o.status }));

        const comingSoon = outlets
            .filter((o) => o.status === 'COMING_SOON')
            .map((o) => ({ value: o._id, label: `${o.name} (${o.city})`, status: o.status }));

        const groups = [];
        if (active.length) groups.push({ label: 'Active Outlets', options: active });
        if (inactive.length) groups.push({ label: 'Inactive Outlets', options: inactive });
        if (comingSoon.length) groups.push({ label: 'Coming Soon', options: comingSoon });

        return groups;
    }, [outlets]);

    // Client-side search filter on table data
    const filteredData = useMemo(() => {
        if (!searchQuery.trim()) return inventoryReportData;
        const q = searchQuery.toLowerCase();
        return inventoryReportData.filter((item) =>
            item?.rawMaterialName?.toLowerCase().includes(q) ||
            item?.unit?.toLowerCase().includes(q)
        );
    }, [inventoryReportData, searchQuery]);

    const getOutlets = async () => {
        try {
            const headers = { Authorization: `Bearer ${accessToken}` };
            const response = await axios.get("outlet", { headers });
            if (response?.data?.status === true) {
                setOutlets(response?.data?.data?.outlets);
            }
        } catch (error) {
            if (error?.response) {
                let errorMessage = "";
                if (error.response?.status === 406) errorMessage = error?.response?.data?.error?.message || error?.response?.data?.error;
                else if (error.response?.status === 412) errorMessage = error?.response?.data?.data?.message;
                else if (error.response?.status === 500) errorMessage = error?.response?.data?.error;
                toast.error(errorMessage, { position: "top-right", autoClose: 3000, theme: "colored" });
            }
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setClicked(true);
        setSearchQuery("");

        const adjustedStartDate = new Date(startDate);
        adjustedStartDate.setHours(23, 59, 59, 999);

        await getOutletInventoryReport(adjustedStartDate, selectedOutlet?.value || "");
    };

    const handleDownloadExcel = async () => {
        setDownloadBtnLoading(true);
        const adjustedStartDate = new Date(startDate);
        adjustedStartDate.setHours(23, 59, 59, 999);
        await getOutletInventoryReport(adjustedStartDate, selectedOutlet?.value || "", true);
    };

    const getOutletInventoryReport = async (date, outlet, download) => {
        setLoading(true);
        try {
            const headers = { Authorization: `Bearer ${accessToken}` };

            let url = `stock-transactions/outletWiseRawMaterialStockReport?date=${date.toISOString().split('T')[0]}&outlet=${outlet}`;
            if (download) url += `&isDownload=${download}`;

            const response = await axios.get(url, {
                headers,
                responseType: download ? 'blob' : 'json'
            });

            if (download) {
                const blob = new Blob([response.data], {
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                });
                const link = document.createElement('a');
                link.href = window.URL.createObjectURL(blob);
                const dateStr = date.toISOString().split('T')[0];
                link.download = `${selectedOutlet?.label || 'All Outlets'} Inventory Report (${dateStr}).xlsx`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(link.href);
                toast.success('Excel file downloaded successfully!', { position: "top-right", autoClose: 3000, theme: "light" });
            } else {
                const result = response?.data?.data?.result;
                setInventoryReportData(result || []);
                toast.success("Report generated successfully", { position: "top-right", autoClose: 2000, theme: "light" });
            }
        } catch (error) {
            setInventoryReportData([]);
            if (error?.response) {
                let errorMessage = "";
                if (error.response?.status === 406) errorMessage = error?.response?.data?.error?.message || error?.response?.data?.error;
                else if (error.response?.status === 404) errorMessage = error?.response?.data?.data?.message;
                else if (error.response?.status === 412) errorMessage = error?.response?.data?.data?.message;
                else if (error.response?.status === 500) errorMessage = error?.response?.data?.error;
                toast.error(errorMessage, { position: "top-right", autoClose: 3000, theme: "light" });
            }
        } finally {
            setLoading(false);
            setDownloadBtnLoading(false);
        }
    };

    const getStockStatus = (item) => {
        if (item?.currentStock <= 0) return { label: "Out of Stock", variant: "danger" };
        if (item?.currentStock < item?.closingStock * 0.2) return { label: "Low Stock", variant: "warning" };
        return { label: "In Stock", variant: "success" };
    };

    useEffect(() => {
        const now = new Date();
        const currentDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const end = new Date(currentDate.setHours(23, 59, 59, 999));
        setStartDate(end);
        getOutlets();
    }, []);

    return (
        <React.Fragment>
            <Header onSkin={setSkin} />
            <div className={`main main-app p-3 p-lg-4 ${skin === "dark" ? "bg-dark text-light" : "bg-light"}`}>
                <Card className="shadow-sm border-0">
                    <Card.Header className="bg-white border-0 py-3 d-flex justify-content-between align-items-center">
                        <div>
                            <h4 className="mb-0 fw-bold d-inline-block">
                                <i className="bi bi-box-seam me-2"></i>OUTLET INVENTORY STATUS
                            </h4>
                        </div>
                        <div>
                            <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={() => navigate('/reports/all-reports')}
                                className="me-3 d-flex align-items-center fw-semibold"
                                style={{ height: '38px' }}
                            >
                                <ArrowLeft className="me-1" /> Back to Reports
                            </Button>
                        </div>
                    </Card.Header>
                    <hr className='mt-0 mb-0' />
                    <Card.Body>
                        {/* Filters Section */}
                        <div className="p-3 bg-light rounded-3">
                            <Form onSubmit={handleSubmit}>
                                <Row className="g-3">

                                    {/* Outlet - react-select with groups */}
                                    <Col md={4}>
                                        <Form.Group controlId="outletSelect">
                                            <Form.Label className="fw-semibold">Outlet</Form.Label>
                                            <Select
                                                isClearable
                                                isSearchable
                                                placeholder="Select Outlet..."
                                                value={selectedOutlet}
                                                onChange={(option) => setSelectedOutlet(option)}
                                                options={outletOptions}
                                                styles={selectStyles}
                                                formatOptionLabel={formatOptionLabel}
                                                noOptionsMessage={() => "No outlets found"}
                                            />
                                            {/* Warning note for non-active outlets */}
                                            {selectedOutlet && selectedOutlet.status !== 'ACTIVE' && (
                                                <Form.Text className={selectedOutlet.status === 'INACTIVE' ? 'text-danger' : 'text-warning'}>
                                                    <i className="bi bi-exclamation-circle me-1"></i>
                                                    {selectedOutlet.status === 'INACTIVE'
                                                        ? 'This outlet is currently inactive.'
                                                        : 'This outlet is coming soon and may not have data yet.'}
                                                </Form.Text>
                                            )}
                                        </Form.Group>
                                    </Col>

                                    {/* Date Picker */}
                                    <Col md={2}>
                                        <Form.Group controlId="startDate">
                                            <Form.Label className="fw-semibold">Select Date</Form.Label>
                                            <DatePicker
                                                selected={startDate}
                                                onChange={(date) => setStartDate(date)}
                                                dateFormat="dd-MM-yyyy"
                                                placeholderText="Select Date"
                                                className="form-control border-secondary"
                                            />
                                        </Form.Group>
                                    </Col>

                                    {/* Generate Button */}
                                    <Col md={2} className="d-flex align-items-end">
                                        <Button
                                            variant="primary"
                                            type="submit"
                                            className="w-100"
                                            disabled={!startDate || !selectedOutlet}
                                        >
                                            {loading ? <Spinner size="sm" animation="border" /> : "Generate Report"}
                                        </Button>
                                    </Col>

                                    {/* Download Button */}
                                    <Col md={2} className="d-flex align-items-end">
                                        <Button
                                            variant="outline-success"
                                            className="w-100 fw-semibold"
                                            onClick={handleDownloadExcel}
                                            disabled={!inventoryReportData?.length}
                                        >
                                            <FileEarmarkSpreadsheet className="me-2" />
                                            {downloadBtnLoading ? <Spinner size='sm' /> : 'Download Excel'}
                                        </Button>
                                    </Col>
                                </Row>
                            </Form>
                        </div>

                        {/* Results Section */}
                        {!clicked ? (
                            <div className="d-flex justify-content-center align-items-center py-5 my-5 bg-light rounded-3 mt-2">
                                <div className="text-center">
                                    <i className="bi bi-clipboard-data fs-1 text-muted"></i>
                                    <h5 className="text-muted mt-3">
                                        Please select filters to view inventory report
                                    </h5>
                                </div>
                            </div>
                        ) : (
                            <>
                                {/* Search bar + record count row */}
                                <div className="d-flex justify-content-between align-items-center mt-3 mb-2">
                                    <div className="text-muted small">
                                        {!loading && inventoryReportData.length > 0 && (
                                            <>
                                                Showing{' '}
                                                <strong>{filteredData.length}</strong> of{' '}
                                                <strong>{inventoryReportData.length}</strong> items
                                                {searchQuery && (
                                                    <span className="ms-1">
                                                        for "<strong>{searchQuery}</strong>"
                                                    </span>
                                                )}
                                            </>
                                        )}
                                    </div>
                                    <div style={{ width: '300px' }}>
                                        <InputGroup size="sm">
                                            <InputGroup.Text className="bg-white border-secondary">
                                                <Search size={13} className="text-muted" />
                                            </InputGroup.Text>
                                            <Form.Control
                                                type="text"
                                                placeholder="Search by raw material or unit..."
                                                value={searchQuery}
                                                onChange={(e) => setSearchQuery(e.target.value)}
                                                className="border-secondary border-start-0"
                                            />
                                            {searchQuery && (
                                                <Button
                                                    variant="outline-secondary"
                                                    onClick={() => setSearchQuery("")}
                                                    style={{ fontSize: '0.75rem', padding: '0 10px' }}
                                                >
                                                    ✕
                                                </Button>
                                            )}
                                        </InputGroup>
                                    </div>
                                </div>

                                {/* Table */}
                                <div className="table-responsive border rounded-3">
                                    <Table striped bordered hover className="mb-0">
                                        <thead className="bg-light">
                                            <tr>
                                                <th width="50">#</th>
                                                <th>Raw Material</th>
                                                <th className="text-center">Unit</th>
                                                <th className="text-center">Opening Stock</th>
                                                <th className="text-center">Received</th>
                                                <th className="text-center">Consumed</th>
                                                <th className="text-center">Wastage</th>
                                                <th className="text-center">Closing Stock</th>
                                                {/* <th className="text-center">Current Stock</th> */}
                                                <th className="text-center">Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {loading ? (
                                                <tr>
                                                    <td colSpan="10" className="text-center py-4">
                                                        <Spinner animation="border" variant="primary" />
                                                        <p className="mt-2 mb-0">Loading inventory data...</p>
                                                    </td>
                                                </tr>
                                            ) : filteredData?.length > 0 ? (
                                                filteredData.map((item, index) => {
                                                    const { label, variant } = getStockStatus(item);
                                                    return (
                                                        <tr key={index}>
                                                            <td className="text-muted">{index + 1}</td>
                                                            <td>
                                                                <div className="fw-semibold">
                                                                    {item?.rawMaterialName || "Unknown Material"}
                                                                </div>
                                                            </td>
                                                            <td className="text-center text-muted">
                                                                {item?.unit || '—'}
                                                            </td>
                                                            <td className="text-center">
                                                                {item?.openingStock?.toFixed(2) ?? '0.00'}
                                                            </td>
                                                            <td className="text-center text-success fw-semibold">
                                                                {item?.received?.toFixed(2) ?? '0.00'}
                                                            </td>
                                                            <td className="text-center text-danger fw-semibold">
                                                                {item?.consumed?.toFixed(2) ?? '0.00'}
                                                            </td>
                                                            <td className="text-center text-warning fw-semibold">
                                                                {item?.wastage?.toFixed(2) ?? '0.00'}
                                                            </td>
                                                            <td className="text-center fw-semibold">
                                                                {item?.closingStock?.toFixed(2) ?? '0.00'}
                                                            </td>
                                                            {/* <td className="text-center fw-semibold text-muted">
                                                                {item?.currentStock?.toFixed(2) ?? '0.00'}
                                                            </td> */}
                                                            <td className="text-center">
                                                                <Badge bg={variant} className="px-1 py-1 w-100 fw-semibold">
                                                                    {label}
                                                                </Badge>
                                                            </td>
                                                        </tr>
                                                    );
                                                })
                                            ) : (
                                                <tr>
                                                    <td colSpan="10" className="text-center py-4">
                                                        <i className="bi bi-exclamation-circle fs-4 text-muted"></i>
                                                        <p className="mt-2 mb-0 text-muted">
                                                            {searchQuery
                                                                ? `No results found for "${searchQuery}"`
                                                                : "No inventory data found for the selected criteria"}
                                                        </p>
                                                        {searchQuery && (
                                                            <Button
                                                                variant="link"
                                                                size="sm"
                                                                className="mt-1 p-0"
                                                                onClick={() => setSearchQuery("")}
                                                            >
                                                                Clear search
                                                            </Button>
                                                        )}
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </Table>
                                </div>
                            </>
                        )}
                    </Card.Body>
                </Card>
            </div>
        </React.Fragment>
    );
};

export default OutletInventoryReport;